import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useApi } from '../hooks/useApi.js';
import { fetchSiteSettings } from '../lib/api.js';

/**
 * Drop-in SEO helper. Pages still use <Helmet> directly for things they
 * own (titles, descriptions); this adds the boilerplate they all need —
 * canonical URL, OpenGraph + Twitter card.
 *
 *   <Seo title="..." description="..." image="..." />
 *
 * Site name + default OG image come from /api/site-settings so the
 * school can change them via the admin without redeploy.
 */
const SITE = {
  // In production, override via VITE_SITE_URL or replace at build time.
  origin: import.meta.env.VITE_SITE_URL || (typeof window !== 'undefined' ? window.location.origin : 'https://mlis.edu.bd'),
  twitter: '@mlisbd',
};

const FALLBACK = {
  name: 'Maple Leaf International School',
  logo: '/img/mlis-logo.jpg',
};

export function Seo({ title, description, image, type = 'website', noIndex = false }) {
  const { pathname } = useLocation();
  const { data: settings } = useApi(fetchSiteSettings, []);

  const siteName = settings?.schoolName || FALLBACK.name;
  const defaultOg = settings?.logo || FALLBACK.logo;

  const canonical = `${SITE.origin}${pathname}`;
  // Absolute URL for OG image — admin uploads return /uploads/... which we
  // need to fully-qualify with the site origin so social sites can fetch it.
  const ogImageRaw = image || defaultOg;
  const ogImage = ogImageRaw.startsWith('http')
    ? ogImageRaw
    : `${SITE.origin}${ogImageRaw}`;

  const fullTitle = title
    ? (title.includes(siteName) ? title : `${title} — ${siteName}`)
    : siteName;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      <link rel="canonical" href={canonical} />
      {noIndex && <meta name="robots" content="noindex,nofollow" />}

      {/* OpenGraph */}
      <meta property="og:site_name" content={siteName} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content="en_GB" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={SITE.twitter} />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={ogImage} />
    </Helmet>
  );
}
